import { Link, useLocation } from 'react-router-dom'
import Protected from '../Protected'
import { PATHS } from '../Routes'
import React, { useEffect, useRef } from 'react'
import useErrorHandler from '../../hooks/useErrorHandler'
import * as Yup from 'yup'
import FormErrorDisplay from '../common/FormErrorMessages'
import { Formik, FormikHelpers, Form } from 'formik'
import { Button } from 'semantic-ui-react'
import FWOTextInput from '../form/FWOTextInput'
import ProfileService from '../../services/ProfileService'
import { NewUserEmail } from '../../models/ChangeUserEmail'
import useLoggedInCookie from '../../hooks/useLoggedInCookie'
import { useMsal } from '@azure/msal-react'
import { MainArticleLayout } from '../../layouts/MainArticleLayout'
import { FormRegex } from '../../models/Common'

const ChangeUserEmailAddress = () => {
  const { instance } = useMsal()
  const [userErrors, , handleErrors, , clearErrors] = useErrorHandler()
  const mountedRef = useRef(false)
  const location = useLocation()
  const { changeUserEmail } = ProfileService
  const [, removeLoggedInCookie] = useLoggedInCookie()

  const queryParams = new URLSearchParams(location.search)

  const validateSchema = Yup.object({
    newEmail: Yup.string()
      .required('New email is required')
      .matches(FormRegex.Email, 'Please enter valid email address'),
  })
  const initialValues = {
    newEmail: '',
    crn: queryParams.get('crn'),
  }

  function handleFormSubmit(
    values: NewUserEmail,
    actions: FormikHelpers<NewUserEmail>
  ) {
    clearErrors()
    changeUserEmail(values)
      .then(() => {
        if (mountedRef.current) {
          //Logout the user
          removeLoggedInCookie()
          instance.logoutRedirect()
        }
      })
      .catch((error) => {
        handleErrors(error)
      })
      .finally(() => {
        if (mountedRef.current) {
          window.scrollTo(0, 0)
          actions.setSubmitting(false)
        }
      })
  }
  useEffect(() => {
    mountedRef.current = true
    if (mountedRef.current) {
      window.scrollTo(0, 0)
    }
    return () => {
      mountedRef.current = false
    }
  }, [])
  return (
    <>
      <Protected>
        <nav className="breadcrumbs dnd-s-mob">
          <ol>
            <li>
              <Link className="underline" to={PATHS.Profile}>
                Back to previous page
              </Link>
            </li>
          </ol>
        </nav>
        <div>
          <MainArticleLayout>
            <h1>Change email address</h1>
            <p></p>
            <h3>What happens after you submit this request?</h3>
            <FormErrorDisplay
              show={userErrors?.length > 0}
              errorsList={userErrors}
              heading="Please review the following errors"
            />
            <p>1. Your email address is set to new email address.</p>
            <p>
              2. On successful change, you will be logged out of current
              session. You will also receive an email notifying you about this
              change and asking you to re-register with new email address.
              Registration link will be in the email. You won&apos;t be able to
              logon anymore using old email address.
            </p>
            <p>
              3. On successful re-registration with new email address, you new
              email address will automatically be linked to your existing my
              account profile. In other words you won&apos;t lose your existing
              data.
            </p>
            <Formik
              validationSchema={validateSchema}
              enableReinitialize
              initialValues={initialValues}
              onSubmit={(values, actions) => handleFormSubmit(values, actions)}
            >
              {({ isValid, isSubmitting, dirty }) => (
                <Form className="ui form osp-variant large" autoComplete="off">
                  <p>
                    <b>Current email address: </b>{' '}
                    {queryParams.get('email')?.trim()?.replace(' ', '+')}
                  </p>
                  <p></p>
                  <FWOTextInput
                    type="email"
                    placeholder="new email"
                    label="New email"
                    ariaLabel="new email"
                    name="newEmail"
                    required={true}
                    aria-label="New email"
                  />
                  <div className="button-group is-right">
                    <Button
                      type="submit"
                      className="is-primary"
                      disabled={isSubmitting || !dirty || !isValid}
                    >
                      Submit
                    </Button>
                    <Button
                      as={Link}
                      to={PATHS.Profile}
                      type="button"
                      content="Cancel"
                      className="is-secondary"
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </MainArticleLayout>
        </div>
      </Protected>
    </>
  )
}

export default ChangeUserEmailAddress
